// src/views/AuthSvcView.js — extracted from App.js
import React, { useState, useRef } from "react";
import { supabase } from "../lib/supabase";
import { compressImage } from "../lib/utils";
import { ADMIN_EMAIL, SK } from "../constants";
import { UPIModal } from "../components/UPIModal";
import { useLanguage, t } from "../lib/i18n";

// ─── AUTH SERVICE VIEW ────────────────────────────────────────────────────────
export function AuthSvcView({ requests, saveRequests, session, openAuth, toast, addNotif, userCredits=0, deductCredit }) {
  const { lang } = useLanguage();
  const [form,setForm]   = useState({watchDesc:"",notes:""});
  const [photos,setPhotos] = useState([]);
  const [done,setDone]   = useState(false);
  const [oid,setOid]     = useState("");
  const [showUPI,setUPI] = useState(false);
  const [uploading,setUpl] = useState(false);
  const photoRef         = useRef(null);
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  const addPhotos = (files) => {
    const incoming = Array.from(files).map(file=>({file,preview:URL.createObjectURL(file)}));
    setPhotos(p=>[...p,...incoming].slice(0,6));
  };
  const removePhoto = (i) => setPhotos(p=>p.filter((_,j)=>j!==i));

  const handlePay = () => {
    if(!session){openAuth();return;}
    if(!form.watchDesc){toast("Describe the watch.");return;}
    setUPI(true);
  };

  const handleUseCredit = async () => {
    if(!session){openAuth();return;}
    if(!form.watchDesc){toast("Describe the watch.");return;}
    const txnId = await deductCredit(session.user.id, 300, null, `Used for authenticity check: ${form.watchDesc}`);
    if (!txnId) { toast("Not enough credit."); return; }
    await handlePaySuccess(txnId);
  };

  const handlePaySuccess = async (txnId) => {
    setUPI(false); setUpl(true);
    const id="AUTH-"+Date.now().toString(36).toUpperCase();
    const u=session.user;
    let photoUrls=[];
    for(let i=0;i<photos.length;i++){
      try{
        const compressed=await compressImage(photos[i].file);
        const path=`auth/${id}/${i}.jpg`;
        const{error}=await supabase.storage.from("watch-photos").upload(path,compressed,{upsert:true,contentType:"image/jpeg"});
        if(!error){const{data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(path);photoUrls.push(publicUrl);}
      }catch{}
    }
    setUpl(false);
    saveRequests([...requests,{id,...form,name:u.name,email:u.email,phone:u.phone||"",watch:form.watchDesc,type:"auth",amount:300,txnId,photos:photoUrls,status:"Received",createdAt:new Date().toISOString(),userId:u.id,updates:[{date:new Date().toISOString(),note:"Auth request received. Payment confirmed."}]}]);
    setOid(id); setDone(true); toast("Submitted — "+id);
    if (addNotif) addNotif("admin", {type:"request", title:`New auth request — ${form.watchDesc||"watch"}`, body:`${u.name} submitted a watch for authenticity verification.`, link:{view:"admin",tab:"requests"}, emailTo:ADMIN_EMAIL, emailSubject:`New authenticity request from ${u.name}`});
  };

  return (
    <div className="fade-in">
      {showUPI && <UPIModal amount={300} purpose="Authenticity Check" onSuccess={handlePaySuccess} onClose={()=>setUPI(false)} session={session}/>}
      {uploading && <div style={{position:"fixed",inset:0,background:"rgba(240,232,216,.85)",zIndex:600,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12}}><div style={{fontFamily:"'Playfair Display',serif",fontSize:36,opacity:.3,animation:"spin 2s linear infinite"}}>◷</div><div style={{fontFamily:"'DM Mono',monospace",fontSize:11,letterSpacing:"0.12em",color:"#8A7E74"}}>UPLOADING PHOTOS…</div></div>}
      <div style={{marginBottom:32}}><span className="sl">{t("auth_sl",lang)}</span>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:40,letterSpacing:"-0.025em",marginBottom:8}}>{t("auth_head",lang)}</h2>
        <p style={{color:"#4A423A",fontSize:14,maxWidth:520,lineHeight:1.75}}>{t("auth_sub2",lang)}</p>
      </div>
      {!done ? (
        <>
          {!session && (
            <div style={{background:"var(--bbg)",border:"1px solid var(--bbd)",borderRadius:10,padding:"14px 18px",marginBottom:20,fontSize:13,color:"var(--ink3)"}}>
              <strong>Sign in first</strong> — we'll use your account details for this request.
            </div>
          )}
          {session && (
            <div style={{background:"rgba(255,255,255,.5)",border:"1px solid var(--bd)",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",gap:12,alignItems:"center",fontSize:13}}>
              <div className="avatar" style={{width:28,height:28,fontSize:10,flexShrink:0}}>{session.user.name.split(" ").map(w=>w[0]).join("").slice(0,2)}</div>
              <div><strong>{session.user.name}</strong> · {session.user.email}</div>
            </div>
          )}
          <div style={{display:"grid",gap:14,marginBottom:14}}>
            <div><label className="fl">{t("auth_watch_lbl",lang)}</label><input value={form.watchDesc} onChange={e=>f("watchDesc",e.target.value)} placeholder={t("auth_watch_ph",lang)} type="text" autoComplete="off" autoCapitalize="words" required/></div>
            <div><label className="fl">{t("auth_doubts_lbl",lang)}</label><textarea value={form.notes} onChange={e=>f("notes",e.target.value)} rows={3} placeholder={t("auth_doubts_ph",lang)} autoCapitalize="sentences"/></div>
            <div>
              <label className="fl">{t("auth_photos_lbl",lang)}</label>
              <div onClick={()=>photoRef.current?.click()} style={{border:"2px dashed var(--bd)",borderRadius:10,padding:"20px 24px",textAlign:"center",cursor:"pointer",background:"rgba(255,255,255,.4)",marginBottom:photos.length?10:0}}>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"#8A7E74"}}>{t("photos_lbl",lang).toUpperCase()} →</div>
                <div style={{fontSize:12,color:"#8A7E74",marginTop:4}}>Dial · case · caseback · movement · serial</div>
              </div>
              <input ref={photoRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>{addPhotos(e.target.files);e.target.value="";}}/>
              {photos.length>0 && (
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {photos.map((p,i)=>(
                    <div key={i} style={{position:"relative"}}>
                      <img src={p.preview} alt="" style={{width:80,height:80,objectFit:"cover",borderRadius:8,border:"1.5px solid var(--bd)"}}/>
                      <button onClick={()=>removePhoto(i)} style={{position:"absolute",top:-6,right:-6,background:"#1A1612",color:"#F0E8D8",border:"none",borderRadius:"50%",width:18,height:18,fontSize:11,lineHeight:"18px",padding:0,cursor:"pointer"}}>×</button>
                    </div>
                  ))}
                  {photos.length<6 && <div onClick={()=>photoRef.current?.click()} style={{width:80,height:80,border:"2px dashed var(--bd)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#8A7E74",fontSize:22}}>+</div>}
                </div>
              )}
            </div>
          </div>
          {userCredits >= 300 && deductCredit ? (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{background:"#F0FDF4",border:"1.5px solid #6EE7B7",borderRadius:12,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.12em",color:"var(--grn)",marginBottom:2}}>₹{userCredits} CREDIT AVAILABLE</div>
                  <div style={{fontSize:12,color:"#065F46"}}>Use your credit — no UPI payment needed</div>
                </div>
                <button style={{background:"var(--grn)",color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontSize:14,cursor:"pointer",fontWeight:600,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}} onClick={handleUseCredit}>{t("use_credit",lang)}</button>
              </div>
              <div style={{textAlign:"center",fontSize:12,color:"var(--ink3)"}}>or <span style={{textDecoration:"underline",cursor:"pointer",color:"var(--ink2)"}} onClick={handlePay}>pay ₹300 via UPI instead</span></div>
            </div>
          ) : (
            <>
              {userCredits > 0 && <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"8px 12px",marginBottom:8,fontSize:12,color:"#92400E"}}>₹{userCredits} credit available — need ₹{300-userCredits} more to cover this fee.</div>}
              <button className="btn-gold" onClick={handlePay}>{t("auth_submit_btn",lang)}</button>
            </>
          )}
        </>
      ) : (
        <div style={{textAlign:"center",padding:"64px 0"}}>
          <div style={{fontSize:52,color:"var(--grn)",fontWeight:400,lineHeight:1,marginBottom:12}}>◈</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:36,marginBottom:8}}>{t("auth_success_h",lang)}</div>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:12,color:"#B8860B",letterSpacing:"0.2em",marginBottom:20}}>{oid}</div>
          <p style={{color:"#4A423A",fontSize:14,maxWidth:440,margin:"0 auto",lineHeight:1.75}}>{t("auth_success_b",lang)}</p>
        </div>
      )}
    </div>
  );
}

// ─── TRACK ────────────────────────────────────────────────────────────────────
